import React, { useState } from 'react';
import { submitOnboarding } from '../api/client';

const SERVICE_CATEGORIES = [
  'Маляр', 'Электрик', 'Сантехник', 'Плиточник',
  'Гипсокартон', 'Фасад/Утепление', 'Кровля', 'Сварка',
  'Отделка', 'Ремонт под ключ', 'Другое',
];

const TEAM_SIZES = [
  { label: 'Работаю один', value: 'Работаю один' },
  { label: 'С напарником', value: 'С напарником' },
  { label: '3 сотрудника', value: '3 сотрудника' },
  { label: '5 и более', value: '5 и более' },
];

export default function OnboardingPage({ onComplete, preview = false }) {
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

  // step: 0,1 = интро-слайды воронки; 2 = анкета
  const [step, setStep] = useState(0);

  const [firstName, setFirstName] = useState(tgUser?.first_name || '');
  const [lastName, setLastName] = useState(tgUser?.last_name || '');
  const [phone, setPhone] = useState('');
  const [categories, setCategories] = useState([]);
  const [customCategory, setCustomCategory] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const toggleCategory = (cat) => {
    setCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const validate = () => {
    const errs = {};
    if (!firstName.trim()) errs.firstName = true;
    if (!lastName.trim()) errs.lastName = true;
    if (!phone.trim()) errs.phone = true;
    if (categories.length === 0) errs.categories = 'Выберите хотя бы одну категорию';
    if (categories.includes('Другое') && !customCategory.trim()) errs.customCategory = true;
    if (!teamSize) errs.teamSize = 'Выберите размер команды';
    return errs;
  };

  const handleSubmit = async () => {
    if (preview) { onComplete(); return; } // превью — без записи в БД
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      const finalCategories = categories.map(c =>
        c === 'Другое' ? customCategory.trim() : c
      );
      await submitOnboarding({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        categories: finalCategories,
        company_name: companyName.trim() || null,
        team_size: teamSize,
        website: website.trim() || null,
      });
      onComplete();
    } catch (err) {
      setErrors({ submit: err.message || 'Ошибка отправки' });
    } finally {
      setLoading(false);
    }
  };

  const chip = (label, selected, onClick) => (
    <div key={label} onClick={onClick} style={{
      padding: '9px 16px',
      borderRadius: 10,
      fontSize: 14,
      fontWeight: selected ? 600 : 400,
      cursor: 'pointer',
      border: selected ? '1.5px solid #FFCC00' : '1px solid #2A2A2A',
      background: selected ? 'rgba(255,204,0,0.12)' : '#141414',
      color: selected ? '#FFCC00' : '#aaa',
      transition: 'all 0.15s ease',
      userSelect: 'none',
    }}>
      {label}
    </div>
  );

  const input = (value, onChange, placeholder, type = 'text', hasError = false) => (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        background: '#141414',
        border: hasError ? '1px solid #FF453A' : '1px solid #2A2A2A',
        borderRadius: 12,
        padding: '14px 16px',
        fontSize: 15,
        color: '#fff',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.15s',
      }}
      onFocus={e => { if (!hasError) e.target.style.borderColor = '#FFCC00'; }}
      onBlur={e => { if (!hasError) e.target.style.borderColor = '#2A2A2A'; }}
    />
  );

  const sectionLabel = (text, required) => (
    <div style={{ fontSize: 13, fontWeight: 600, color: '#888', marginBottom: 10, letterSpacing: 0.3, textTransform: 'uppercase' }}>
      {text}{required && <span style={{ color: '#FFCC00', marginLeft: 4 }}>*</span>}
    </div>
  );

  const errorText = (msg) => msg ? (
    <div style={{ fontSize: 12, color: '#FF453A', marginTop: 6 }}>{msg}</div>
  ) : null;

  // ── Интро-воронка: 2 слайда перед анкетой ──────────────────────────────
  const progressDots = (active) => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 7, marginBottom: 28 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: i === active ? 22 : 7, height: 7, borderRadius: 4,
          background: i === active ? '#FFCC00' : '#2A2A2A',
          transition: 'all 0.25s ease',
        }} />
      ))}
    </div>
  );

  const introWrap = (children) => (
    <div style={{
      minHeight: '100dvh', background: '#0D0D0D',
      paddingTop: 'calc(var(--tg-safe-top, 0px) + 24px)',
      paddingBottom: 'calc(var(--tg-safe-bottom, 0px) + 32px)',
      color: '#fff', display: 'flex', flexDirection: 'column',
    }}>{children}</div>
  );

  const introButton = (label, onClick) => (
    <button onClick={onClick} style={{
      width: '100%', background: '#FFCC00', color: '#0D0D0D', border: 'none',
      borderRadius: 14, padding: '16px 0', fontSize: 16, fontWeight: 700,
      cursor: 'pointer', letterSpacing: 0.3,
    }}>{label}</button>
  );

  // Слайд 1 — логотип + миссия
  if (step === 0) {
    return introWrap(
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 24px' }}>
        {progressDots(0)}
        <div key="s0" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', animation: 'fadeInUp 0.35s ease both' }}>
          <img src="/logo.png" alt="Fix-Hub" style={{ height: 56, objectFit: 'contain', marginBottom: 12 }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: '#888', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 20 }}>
            Молдова
          </div>
          <div style={{ fontSize: 21, fontWeight: 700, lineHeight: 1.35, marginBottom: 16 }}>
            Создаём крупнейшее сообщество строителей и ремонтников Молдовы
          </div>
          <div style={{ fontSize: 15, color: '#888', lineHeight: 1.5, maxWidth: 320, margin: '0 auto' }}>
            Прямые заказы, обмен опытом, проверенные эксперты и полезные материалы — всё в одном месте.
          </div>
        </div>
        <div style={{ paddingTop: 20 }}>{introButton('Далее →', () => setStep(1))}</div>
      </div>
    );
  }

  // Слайд 2 — что внутри
  if (step === 1) {
    const benefits = [
      { color: '#FFCC00', title: 'Прямые заказы', text: 'без посредников и комиссий',
        paths: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></> },
      { color: '#0A84FF', title: 'Обмен объектами', text: 'делитесь работой с участниками',
        paths: <><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></> },
      { color: '#30D158', title: 'Ответы экспертов', text: 'налоги, право, расценки, договоры',
        paths: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></> },
      { color: '#FF9F0A', title: 'События и материалы', text: 'вебинары, гайды, шаблоны',
        paths: <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 0 3-3h7z" /></> },
    ];
    return introWrap(
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 24px' }}>
        {progressDots(1)}
        <div key="s1" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', animation: 'fadeInUp 0.35s ease both' }}>
          <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 8, textAlign: 'center', letterSpacing: -0.5 }}>
            Что вас ждёт внутри
          </div>
          <div style={{ fontSize: 14, color: '#888', textAlign: 'center', marginBottom: 24 }}>
            Вы — среди первых, кто формирует это сообщество.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {benefits.map((b, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                background: '#141414', border: '1px solid #2A2A2A',
                borderRadius: 14, padding: '14px 16px',
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                  background: b.color + '1F',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke={b.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {b.paths}
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{b.title}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>{b.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {introButton('Заполнить анкету →', () => setStep(2))}
          <button onClick={() => setStep(0)} style={{
            width: '100%', background: 'transparent', color: '#666', border: 'none',
            padding: '8px 0', fontSize: 14, cursor: 'pointer',
          }}>Назад</button>
        </div>
      </div>
    );
  }

  // step === 2 — анкета
  return (
    <div style={{
      minHeight: '100dvh',
      background: '#0D0D0D',
      paddingTop: 'calc(var(--tg-safe-top, 0px) + 20px)',
      paddingBottom: 'calc(var(--tg-safe-bottom, 0px) + 40px)',
      color: '#fff',
    }}>
      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '4px 24px 28px' }}>
        {progressDots(2)}
        <img src="/logo.png" alt="Fix-Hub" style={{ height: 36, objectFit: 'contain', marginBottom: 14 }} />
        <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
          Последний шаг
        </div>
        <div style={{ fontSize: 15, color: '#888', lineHeight: 1.5, maxWidth: 300, margin: '0 auto' }}>
          Расскажите о себе — и доступ к сообществу откроется.
        </div>
        <div onClick={() => setStep(1)} style={{ fontSize: 14, color: '#666', marginTop: 14, cursor: 'pointer' }}>← Назад</div>
      </div>

      {/* Form */}
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 28 }}>

        {/* Personal */}
        <div>
          {sectionLabel('Личные данные', true)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {input(firstName, setFirstName, 'Имя', 'text', errors.firstName)}
            {input(lastName, setLastName, 'Фамилия', 'text', errors.lastName)}
            {input(phone, setPhone, '+373 ...', 'tel', errors.phone)}
          </div>
        </div>

        {/* Categories */}
        <div>
          {sectionLabel('Категория услуг', true)}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SERVICE_CATEGORIES.map(cat =>
              chip(cat, categories.includes(cat), () => toggleCategory(cat))
            )}
          </div>
          {categories.includes('Другое') && (
            <div style={{ marginTop: 10 }}>
              {input(customCategory, setCustomCategory, 'Укажите категорию', 'text', errors.customCategory)}
            </div>
          )}
          {errorText(errors.categories)}
        </div>

        {/* Team size */}
        <div>
          {sectionLabel('Размер команды', true)}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TEAM_SIZES.map(({ label, value }) =>
              chip(label, teamSize === value, () => setTeamSize(value))
            )}
          </div>
          {errorText(errors.teamSize)}
        </div>

        {/* Optional */}
        <div>
          {sectionLabel('Дополнительно', false)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {input(companyName, setCompanyName, 'Название фирмы')}
            {input(website, setWebsite, 'Вебсайт (если есть)', 'url')}
          </div>
        </div>

        {/* Submit */}
        <div style={{ paddingTop: 4 }}>
          {errors.submit && (
            <div style={{ fontSize: 13, color: '#FF453A', textAlign: 'center', marginBottom: 12 }}>
              {errors.submit}
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? 'rgba(255,204,0,0.5)' : '#FFCC00',
              color: '#0D0D0D',
              border: 'none',
              borderRadius: 14,
              padding: '16px 0',
              fontSize: 16,
              fontWeight: 700,
              cursor: loading ? 'default' : 'pointer',
              transition: 'background 0.2s',
              letterSpacing: 0.3,
            }}
          >
            {loading ? 'Отправка...' : 'Начать работу →'}
          </button>
        </div>
      </div>
    </div>
  );
}
